/**
 * CategoryBar - Tab-style category navigation with optional admin Create button.
 */
import { type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { useTheme } from '@mui/material/styles';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AnnouncementIcon from '@mui/icons-material/Announcement';
import NotificationsIcon from '@mui/icons-material/Notifications';

export type CategoryFilter = 'news' | 'notifications' | 'tasks' | 'incidents';

const CATEGORIES: {
  value: CategoryFilter;
  label: string;
  icon: ReactNode;
}[] = [
  {
    value: 'news',
    label: 'News',
    icon: <NewspaperIcon sx={{ fontSize: 20 }} />,
  },
  {
    value: 'notifications',
    label: 'Notifications',
    icon: <NotificationsIcon sx={{ fontSize: 20 }} />,
  },
  {
    value: 'tasks',
    label: 'Tasks',
    icon: <AssignmentIcon sx={{ fontSize: 20 }} />,
  },
  {
    value: 'incidents',
    label: 'Incidents',
    icon: <AnnouncementIcon sx={{ fontSize: 20 }} />,
  },
];

interface CategoryBarProps {
  selectedCategories: string[];
  onCategoryChange: (value: CategoryFilter) => void;
  onCreateClick?: () => void;
}

export function CategoryBar({
  selectedCategories,
  onCategoryChange,
  onCreateClick,
}: CategoryBarProps) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        width: '100%',
        borderBottom: `2px solid ${theme.palette.divider}`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 0,
        }}
      >
        {CATEGORIES.map(({ value, label, icon }) => {
          const selected = selectedCategories.includes(value);
          return (
            <Box
              key={value}
              role="button"
              tabIndex={0}
              onClick={() => onCategoryChange(value)}
              onKeyDown={e => e.key === 'Enter' && onCategoryChange(value)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: 2.5,
                py: 1.5,
                minHeight: 48,
                marginBottom: '-2px',
                cursor: 'pointer',
                userSelect: 'none',
                background: 'transparent',
                border: 'none',
                borderBottom: selected
                  ? `2px solid ${theme.palette.primary.main}`
                  : '2px solid transparent',
                color: selected
                  ? theme.palette.primary.main
                  : theme.palette.text.secondary,
                fontWeight: selected ? 650 : 500,
                fontSize: '0.9rem',
                borderRadius: 0,
                transition: 'color 0.15s, border-bottom-color 0.15s',
                '&:hover': { background: theme.palette.action.hover },
              }}
            >
              {icon}
              <span>{label}</span>
            </Box>
          );
        })}
      </Box>

      {onCreateClick && (
        <Button
          variant="contained"
          size="small"
          sx={{ borderRadius: 20 }}
          onClick={onCreateClick}
        >
          Manage Communications
        </Button>
      )}
    </Box>
  );
}
